import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import BrandHeader from "../../components/BrandHeader";
import { abandonedApi } from "../../lib/api";

const WHATSAPP_TPL = (name, lp, crystal) =>
  `\u{1F338} Hi ${name || "there"}, this is Madhuram from Pancha Healing. I noticed you started your crystal prescription today but didn't complete payment. Your Life Path number is ${lp ?? "—"} and your birth month stone is ${crystal || "—"}. If you have any questions — just reply. Your link is still active: panchahealing.com — Madhuram`;

function timeAgo(hours) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min ago`;
  if (hours < 24) return `${Math.round(hours)} hours ago`;
  const d = Math.round(hours / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

function badgeColor(hours) {
  if (hours < 24) return { bg: "#fff3cd", text: "#8a6d00", label: "Under 24h" };
  if (hours <= 72) return { bg: "#ffe4b5", text: "#a0540b", label: "24-72h" };
  return { bg: "#fde1df", text: "#a02626", label: "Over 72h" };
}

const STAGE_LABEL = {
  completed_basics: "Quiz · Step 1",
  completed_birth_details: "Quiz · Step 2",
  completed_quiz: "Quiz · Step 3",
  reached_payment: "Payment screen",
};

export default function AbandonedPrescriptions() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_abandoned: 0, recovered: 0, recovery_rate: 0, revenue_recovered: 0 });
  const [popupStats, setPopupStats] = useState({ shown: 0, dismissed: 0, converted: 0, conversion_rate: 0 });
  const [recoveryStats, setRecoveryStats] = useState({ clicks: 0, converted: 0, conversion_rate: 0, avg_time_to_pay_minutes: 0 });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("month");
  const [status, setStatus] = useState("all");
  const [stage, setStage] = useState("all");
  const [profileOrder, setProfileOrder] = useState(null);
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [list, s, ps, rs] = await Promise.all([
        abandonedApi.list({ range, status, stage }),
        abandonedApi.stats(),
        abandonedApi.popupStats(),
        abandonedApi.recoveryStats(),
      ]);
      setRows(list);
      setStats(s);
      setPopupStats(ps);
      setRecoveryStats(rs);
    } catch {
      toast.error("Failed to load abandoned list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch is fire-and-forget; state updates happen after the awaited promise resolves,
    // which is the idiomatic pattern (not the synchronous setState-in-effect the rule guards against).
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, s, ps, rs] = await Promise.all([
          abandonedApi.list({ range, status, stage }),
          abandonedApi.stats(),
          abandonedApi.popupStats(),
          abandonedApi.recoveryStats(),
        ]);
        if (cancelled) return;
        setRows(list);
        setStats(s);
        setPopupStats(ps);
        setRecoveryStats(rs);
      } catch {
        if (!cancelled) toast.error("Failed to load abandoned list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range, status, stage]);

  const copyMsg = (r) => {
    const txt = WHATSAPP_TPL(r.fullName, r.life_path, r.birth_month_crystal);
    const digits = (r.whatsapp || "").replace(/\D/g, "");
    const waLink = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(txt)}` : "";
    if (waLink) {
      navigator.clipboard.writeText(`${waLink}\n\n${txt}`);
      toast.success("WhatsApp link + message copied");
      window.open(waLink, "_blank");
    } else {
      navigator.clipboard.writeText(txt);
      toast.success("Message copied (no phone on file)");
    }
  };

  const sendEmail = async (r, n) => {
    setSending((p) => ({ ...p, [r.id + "-" + n]: true }));
    try {
      const res = await abandonedApi.sendEmail(r.id, n);
      if (res.ok) toast.success(`Email ${n} sent to ${r.email}`);
      else toast.error(`Failed: ${res.error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending((p) => ({ ...p, [r.id + "-" + n]: false }));
    }
  };

  const markContacted = async (r) => {
    try {
      await abandonedApi.markContacted(r.id, !r.contacted);
      toast.success(r.contacted ? "Unmarked" : "Marked as contacted");
      await load();
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div className="min-h-screen" data-testid="abandoned-page">
      <BrandHeader adminMode />
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="ph-overline">Recovery</div>
            <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">Abandoned Prescriptions</h1>
          </div>
          <button onClick={load} className="ph-btn-ghost text-sm" data-testid="abandoned-refresh">Refresh</button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total abandoned · month" value={stats.total_abandoned} testid="stat-total" />
          <StatCard label="Recovered · month" value={stats.recovered} testid="stat-recovered" />
          <StatCard label="Recovery rate" value={`${stats.recovery_rate}%`} testid="stat-rate" />
          <StatCard label="Revenue recovered" value={`Rs ${Number(stats.revenue_recovered || 0).toLocaleString("en-IN")}`} testid="stat-revenue" highlight />
        </div>

        {/* Exit-intent popup stats */}
        <div className="ph-card p-5 mb-6" data-testid="popup-stats">
          <div className="ph-overline">Exit-intent popup · this month</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <MiniStat label="Shown" value={popupStats.shown} />
            <MiniStat label="Dismissed" value={popupStats.dismissed} />
            <MiniStat label="Converted" value={popupStats.converted} />
            <MiniStat label="Conversion rate" value={`${popupStats.conversion_rate}%`} highlight />
          </div>
        </div>

        {/* Email deep-link recovery */}
        <div className="ph-card p-5 mb-6" data-testid="recovery-stats">
          <div className="ph-overline">Recovery email deep-links · this month</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <MiniStat label="Clicks" value={recoveryStats.clicks} />
            <MiniStat label="Converted" value={recoveryStats.converted} />
            <MiniStat label="Recovery conversion" value={`${recoveryStats.conversion_rate}%`} highlight />
            <MiniStat label="Avg. time to pay" value={`${recoveryStats.avg_time_to_pay_minutes} min`} />
          </div>
        </div>

        {/* Filters */}
        <div className="ph-card p-4 md:p-5 flex flex-wrap gap-3 items-center mb-4">
          <select className="ph-input max-w-[180px]" value={range} onChange={(e) => setRange(e.target.value)} data-testid="filter-range">
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>
          </select>
          <select className="ph-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
            <option value="all">All statuses</option>
            <option value="not_contacted">Not Contacted</option>
            <option value="contacted">Contacted</option>
            <option value="recovered">Recovered</option>
          </select>
          <select className="ph-input max-w-[200px]" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="filter-stage">
            <option value="all">All drop-off stages</option>
            <option value="quiz">Quiz</option>
            <option value="payment">Payment screen</option>
          </select>
          <div className="ml-auto text-sm text-[#6B5E7E]">{rows.length} records</div>
        </div>

        {/* Table (desktop) / Cards (mobile) */}
        <div className="ph-card overflow-hidden">
          {loading ? (
            <p className="p-6 text-[#6B5E7E]">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-[#6B5E7E]">No abandoned prescriptions match these filters.</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="abandoned-table">
                  <thead>
                    <tr className="text-left text-[#6B5E7E] bg-[#FAF8F4]">
                      <th className="py-3 px-3">Customer</th>
                      <th className="py-3 px-3">Email</th>
                      <th className="py-3 px-3">Phone</th>
                      <th className="py-3 px-3">DOB</th>
                      <th className="py-3 px-3">LP</th>
                      <th className="py-3 px-3">Birth Crystal</th>
                      <th className="py-3 px-3">Stage</th>
                      <th className="py-3 px-3">Dropped</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const bc = badgeColor(r.hours_since_dropoff || 0);
                      return (
                        <tr key={r.id} className="border-t border-[rgba(106,13,173,0.06)] hover:bg-[#FAF8F4]">
                          <td className="py-3 px-3 text-[#2C0A5E]">{r.fullName || <em className="text-[#9e94ae]">unnamed</em>}</td>
                          <td className="py-3 px-3 text-[#6B5E7E] truncate max-w-[180px]">{r.email || "-"}</td>
                          <td className="py-3 px-3 text-[#6A0DAD]"><a href={`tel:${(r.whatsapp || "").replace(/\s/g, "")}`} className="hover:underline">{r.whatsapp || "-"}</a></td>
                          <td className="py-3 px-3 text-[#6B5E7E]">{r.dob || "-"}</td>
                          <td className="py-3 px-3 text-[#6A0DAD]">{r.life_path ?? "-"}</td>
                          <td className="py-3 px-3 text-[#6B5E7E]">{r.birth_month_crystal || "-"}</td>
                          <td className="py-3 px-3 text-[#6B5E7E]">{STAGE_LABEL[r.status] || r.status}</td>
                          <td className="py-3 px-3 text-[#6B5E7E]">{timeAgo(r.hours_since_dropoff || 0)}</td>
                          <td className="py-3 px-3">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bc.bg, color: bc.text }}>{bc.label}</span>
                            {r.contacted && <span className="ml-1 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E1F5FE] text-[#01579B]">Contacted</span>}
                            {r.recovered && <span className="ml-1 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E8F5E9] text-[#1B5E20]">Recovered</span>}
                          </td>
                          <td className="py-3 px-3">
                            <RowActions r={r} copyMsg={copyMsg} sendEmail={sendEmail} markContacted={markContacted} sending={sending} onView={() => setProfileOrder(r)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-[rgba(106,13,173,0.08)]">
                {rows.map((r) => {
                  const bc = badgeColor(r.hours_since_dropoff || 0);
                  return (
                    <div key={r.id} className="p-4" data-testid={`abandoned-card-${r.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-serif text-lg text-[#2C0A5E]">{r.fullName || "unnamed"}</div>
                          <div className="text-xs text-[#6B5E7E]">{r.email || "no email"}</div>
                          <a href={`tel:${(r.whatsapp || "").replace(/\s/g, "")}`} className="text-xs text-[#6A0DAD]">{r.whatsapp || ""}</a>
                        </div>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bc.bg, color: bc.text }}>{bc.label}</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-y-1 text-xs text-[#6B5E7E]">
                        <div>DOB: <span className="text-[#2C0A5E]">{r.dob || "-"}</span></div>
                        <div>LP: <span className="text-[#6A0DAD]">{r.life_path ?? "-"}</span></div>
                        <div>Crystal: <span className="text-[#2C0A5E]">{r.birth_month_crystal || "-"}</span></div>
                        <div>Dropped: <span className="text-[#2C0A5E]">{timeAgo(r.hours_since_dropoff || 0)}</span></div>
                      </div>
                      <div className="mt-2 text-xs text-[#6B5E7E]">Stage: {STAGE_LABEL[r.status] || r.status}</div>
                      <div className="mt-3">
                        <RowActions r={r} copyMsg={copyMsg} sendEmail={sendEmail} markContacted={markContacted} sending={sending} onView={() => setProfileOrder(r)} compact />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {profileOrder && (
        <ProfileModal order={profileOrder} onClose={() => setProfileOrder(null)} />
      )}
    </div>
  );
}

function _relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function RowActions({ r, copyMsg, sendEmail, markContacted, sending, onView, compact = false }) {
  const btnCls = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border border-[#D4A855] text-[#6A0DAD] hover:bg-[#FAF3E3] transition";
  const btnDoneCls = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border border-[rgba(106,13,173,0.15)] bg-[#F3F0E8] text-[#8B7B6E] cursor-not-allowed line-through";
  const log = r.emailLog || [];
  const lastSent = log.length ? log[log.length - 1] : null;
  const sentSteps = new Set(log.map((e) => e.step));

  const emailButton = (n) => {
    const already = sentSteps.has(n);
    const isSending = sending[r.id + "-" + n];
    return (
      <button
        className={already ? btnDoneCls : btnCls}
        disabled={!r.email || isSending || already}
        onClick={() => sendEmail(r, n)}
        title={already ? `Already sent (step ${n})` : `Send email ${n} to ${r.email || "no email"}`}
        data-testid={`send-email-${n}-${r.id}`}
      >
        {isSending ? "..." : already ? `✓ Email ${n}` : `Email ${n}`}
      </button>
    );
  };

  return (
    <div className={`flex flex-col gap-1.5 ${compact ? "" : "max-w-[340px]"}`}>
      <div className="flex flex-wrap gap-1.5">
        <button className={btnCls} onClick={() => copyMsg(r)} data-testid={`copy-wa-${r.id}`}>Copy WA</button>
        {emailButton(1)}
        {emailButton(2)}
        {emailButton(3)}
        <button className={btnCls} onClick={() => markContacted(r)} data-testid={`mark-contacted-${r.id}`}>{r.contacted ? "Unmark" : "Mark contacted"}</button>
        <button className={btnCls} onClick={onView} data-testid={`view-profile-${r.id}`}>Profile</button>
      </div>
      {lastSent && (
        <div
          className="text-[10px] text-[#6B5E7E] italic"
          data-testid={`email-status-${r.id}`}
          title={`Last sent: ${new Date(lastSent.sentAt).toLocaleString("en-IN")}`}
        >
          Email {lastSent.step} sent · {_relativeTime(lastSent.sentAt)}
          {lastSent.manual ? " · manual" : " · auto"}
          {lastSent.clicked ? " · clicked ✓" : lastSent.opened ? " · opened" : ""}
          {log.length > 1 && ` · ${log.length} of 3 sent`}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testid, highlight }) {
  return (
    <div className="ph-card p-5" data-testid={testid}>
      <div className="ph-overline">{label}</div>
      <div className={`font-serif mt-2 ${highlight ? "text-2xl text-[#6A0DAD]" : "text-2xl text-[#2C0A5E]"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.2em] uppercase text-[#6B5E7E]">{label}</div>
      <div className={`font-serif text-xl mt-1 ${highlight ? "text-[#6A0DAD]" : "text-[#2C0A5E]"}`}>{value}</div>
    </div>
  );
}

function ProfileModal({ order, onClose }) {
  const rows = [
    ["Full Name", order.fullName],
    ["Email", order.email],
    ["Phone", order.whatsapp],
    ["DOB", order.dob],
    ["Time of Birth", order.timeOfBirth],
    ["Birth City", order.birthCity],
    ["Life Path", order.life_path],
    ["Birth Month", order.birth_month],
    ["Birth Crystal", order.birth_month_crystal],
    ["Zodiac", order.numerology?.zodiac || order.zodiac_manual],
    ["Intentions", (order.intentions || []).join(", ")],
    ["Other intention", order.otherIntention],
    ["Notes", order.notes],
    ["Status", order.status],
    ["Started at", order.started_at || order.createdAt],
    ["Last active", order.last_active],
  ];
  const emails = order.emails_sent || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[rgba(106,13,173,0.1)] flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#2C0A5E]">Customer profile</h2>
          <button className="text-[#6B5E7E] hover:text-[#6A0DAD] text-xl leading-none" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="p-5 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3">
            {rows.map(([k, v]) => (
              <>
                <div key={k + "-l"} className="text-[#6B5E7E]">{k}</div>
                <div key={k + "-v"} className="text-[#2C0A5E] break-words">{v == null || v === "" ? "-" : String(v)}</div>
              </>
            ))}
          </div>
          {emails.length > 0 && (
            <div className="mt-5">
              <div className="ph-overline">Emails sent</div>
              <ul className="mt-2 space-y-1">
                {emails.map((e, i) => (
                  <li key={i} className="text-xs text-[#6B5E7E]">
                    <strong className="text-[#2C0A5E]">{e.type}</strong> · {new Date(e.sent_at).toLocaleString()} · {e.ok ? "delivered" : "failed"}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
